// The learn routes, parsed at the boundary: GET and PUT /v1/learn/progress,
// GET /v1/learn/drills/due and the journal with flight notes. Everything goes
// through edgeApi's call(), so the device header goes out and the analytics
// identity comes back from every response.
import {
  MARKER_OUT_OF_BUDGET_CODE,
  MarkError,
  MarkRecord,
  MarkRemarkRequest,
  MarkRequest,
  OkResponse,
} from "@proef/contract";
import { call, EdgeApiError } from "../edgeApi";
import type { MarkingFailure } from "./analytics";
import {
  ClaimLearnProgressResponse,
  DrillsDue,
  LearnProgress,
  LearnPutRefused,
  ListLearnTastingsResponse,
  ProgressItem,
  PutProgressRequest,
} from "./contract";

// `shop=1` says this bundle can read shopping ticks and `mastery=1` enrolments, mock weeks and
// badges; a bundle from before either is never sent a key it could not parse.
const PROGRESS = "/v1/learn/progress?shop=1&mastery=1";

export function getProgress(): Promise<LearnProgress> {
  return call(LearnProgress, PROGRESS);
}

/** Sends `items` with their own `updatedAt`; the response is the merged progress the mirror adopts. */
export async function putProgress(items: readonly ProgressItem[]): Promise<LearnProgress> {
  // Parsed first, so a request the edge would refuse never leaves the device (and rejects, not throws).
  const body = PutProgressRequest.parse({ items });
  return await call(LearnProgress, PROGRESS, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/** Parity with the device's own due list; the screens compute it locally. */
export function getDrillsDue(): Promise<DrillsDue> {
  return call(DrillsDue, "/v1/learn/drills/due");
}

/** The note picker's and the auto-attach's source: solo notes plus the learner's own flight notes. */
export function listTastingsWithFlight(
  limit = 100,
  offset = 0,
): Promise<ListLearnTastingsResponse> {
  return call(
    ListLearnTastingsResponse,
    `/v1/tastings?include=flight&limit=${String(limit)}&offset=${String(offset)}`,
  );
}

/** The keys a refused PUT (400 or 422) set aside; empty for anything else, so the caller retries. */
export function refusedKeys(error: unknown): string[] {
  if (!(error instanceof EdgeApiError) || (error.status !== 400 && error.status !== 422)) return [];
  const parsed = LearnPutRefused.safeParse(error.details);
  return parsed.success ? parsed.data.refused.map((entry) => entry.key) : [];
}

// ---- the account's progress (ticket 219, Q16) ---------------------------------------

/** A signed-in learner's progress lives on the account; these routes answer 401 without a session. */
export function getAccountProgress(): Promise<LearnProgress> {
  return call(LearnProgress, "/v1/learn/account/progress");
}

export async function putAccountProgress(items: readonly ProgressItem[]): Promise<LearnProgress> {
  const body = PutProgressRequest.parse({ items });
  return await call(LearnProgress, "/v1/learn/account/progress", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/** Brings this device's progress into the account; the device's rows are hidden once taken. */
export function claimProgress(): Promise<ClaimLearnProgressResponse> {
  return call(ClaimLearnProgressResponse, "/v1/learn/account/progress/claim", { method: "POST" });
}

// ---- marking (the shared marker, POST /v1/learn/mark) -------------------------------

export type MarkingOutcome =
  | { readonly state: "marked"; readonly record: MarkRecord }
  | {
      readonly state: "pending";
      readonly reason: MarkingFailure;
      readonly markId?: string;
    };

const codeOf = (error: EdgeApiError): string | undefined => {
  const body: unknown = error.body;
  if (typeof body !== "object" || body === null || !("code" in body)) return undefined;
  return typeof body.code === "string" ? body.code : undefined;
};

/** The fixed word for why a marking waits; the provider's own words never reach the app or analytics. */
export function markingFailure(error: unknown): MarkingFailure {
  if (!(error instanceof EdgeApiError)) return "timeout";
  const code = codeOf(error);
  if (code === MARKER_OUT_OF_BUDGET_CODE) return "out_of_budget";
  if (error.status === 429) return "rate_limited";
  return "unavailable";
}

/**
 * Hands in one flight's written answers. The marker that cannot answer is not an error to the learner:
 * the hand-in is stored on the edge and comes back as pending, and the sitting goes on.
 */
export async function handInForMarking(request: MarkRequest): Promise<MarkingOutcome> {
  const body = MarkRequest.parse(request);
  try {
    const record = await call(MarkRecord, "/v1/learn/mark", {
      method: "POST",
      body: JSON.stringify(body),
    });
    return { state: "marked", record };
  } catch (error) {
    if (error instanceof EdgeApiError && (error.status === 503 || error.status === 429)) {
      const pending = MarkError.safeParse(error.body);
      const markId = pending.success ? pending.data.markId : undefined;
      return {
        state: "pending",
        reason: markingFailure(error),
        ...(markId === undefined ? {} : { markId }),
      };
    }
    throw error;
  }
}

export function getMarking(markId: string): Promise<MarkRecord> {
  return call(MarkRecord, `/v1/learn/mark/${encodeURIComponent(markId)}`);
}

/** An appeal is criterion ids and nothing else. */
export function remarkAnswer(markId: string, request: MarkRemarkRequest): Promise<MarkRecord> {
  const body = MarkRemarkRequest.parse(request);
  return call(MarkRecord, `/v1/learn/mark/${encodeURIComponent(markId)}/remark`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteMarking(markId: string): Promise<OkResponse> {
  return call(OkResponse, `/v1/learn/mark/${encodeURIComponent(markId)}`, { method: "DELETE" });
}
